import { Injectable } from "@angular/core";
import { MatchRepo } from "@app/core/match/domain/match.repo";
import { Observable, of } from "rxjs";
import { Match } from "@app/core/match/domain/model/match";
import { MatchWS } from "@app/core/match/data/network/match.ws";
import { MatchSearchCriteria } from "@app/core/match/domain/match-search-criteria";
import { PaginatedResponse } from "@app/core/shared/domain/paginated-response";
import { MatchDetailWS } from "@app/core/match/data/network/match-detail.ws";
import { MatchDetail } from "@app/core/match/domain/model/match-detail";
import { PlayerAlreadySelectedWS } from "../network/player-already-selected.ws";
import { GetCoachPhoneNumberWS } from "@app/core/coach/infrastructure/get-coach-phone-number.ws";
import { getPublicVideoLinkWS } from "@app/core/match/data/network/get-public-video-link.ws";
import { MatchPaymentInfo } from "@app/core/match/domain/model/match-payment-info";
import { MatchPaymentInfoWS } from "@app/core/match/data/network/match-payment-info.ws";
import { MatchDetailSampleWS } from "../network/match-detail.sample.ws";
import { MatchCompareResponse, MatchCompareWS } from "../network/match-compare.ws";
import { MatchShareStatisticsWS } from "../network/match-share-statistics.ws";
import { ShareStatistics } from "../../domain/model/share-statistics";
import { SharedStatisticsWS } from "../network/shared-statistics.ws";
import { CompetitiveWS } from "../network/competitive.ws";
import { MatchLeaveWS } from "../network/match-leave.ws";

/**
 * Repository used to get match detail.
 */
@Injectable()
export class MatchDataRepo implements MatchRepo {
  constructor(
    private matchDetailWS: MatchDetailWS,
    private matchCompareWS: MatchCompareWS,
    private matchDetailSampleWS: MatchDetailSampleWS,
    private MatchWS: MatchWS,
    private playerAlreadySelectedWS: PlayerAlreadySelectedWS,
    private getCoachPhoneNumberWS: GetCoachPhoneNumberWS,
    private getPublicVideoLinkWS: getPublicVideoLinkWS,
    private matchPaymentInfoWS: MatchPaymentInfoWS,
    private matchShareStatisticsWS: MatchShareStatisticsWS,
    private sharedStatisticsWS: SharedStatisticsWS,
    private competitive: CompetitiveWS,
    private matchLeaveWS: MatchLeaveWS,
  ) {}

  /**
   * Get match detail.
   * @param matchId
   * @param token user token authentication
   */
  getMatchDetail(matchId: string, token: string): Observable<MatchDetail> {
    return this.matchDetailWS.execute(matchId, token);
  }

  getMatchCompare(matchId: string, token: string): Observable<MatchCompareResponse> {
    return this.matchCompareWS.execute(matchId, token);
  }

  getMatchDetailSample(token: string): Observable<MatchDetail> {
    return this.matchDetailSampleWS.execute(token);
  }

  getMatches(
    matchSearchCriteria: MatchSearchCriteria,
    token: string
  ): Observable<PaginatedResponse<Match[]>> {
    return this.MatchWS.execute(matchSearchCriteria, token);
  }

  isPlayerAlreadySelectedInMatch(
    matchId: string,
    token: string
  ): Observable<boolean> {
    return this.playerAlreadySelectedWS.execute(matchId, token);
  }

  getCoachPhoneNumber(token: string, matchid: string): Observable<string> {
    return this.getCoachPhoneNumberWS.execute(token, matchid);
  }

  getPublicVideoLink(
    matchId: string,
    type: "short" | "extended",
    token: string
  ): Observable<string> {
    return this.getPublicVideoLinkWS.execute(matchId, type, token);
  }

  getMatchPaymentInfo(
    matchId: string,
    token: string
  ): Observable<MatchPaymentInfo> {
    return this.matchPaymentInfoWS.execute(matchId, token);
  }

  shareStatistics(
    matchId: string,
    token: string
  ): Observable<ShareStatistics> {
    return this.matchShareStatisticsWS.execute(matchId, token);
  }

  getSharedStatistics(
    playerId: string,
    token: string
  ): Observable<MatchDetail> {
    return this.sharedStatisticsWS.execute(playerId, token);
  }

  setCompetitive(
    pk: string,
    competitive: boolean,
    token: string
  ): Observable<void> {
    return this.competitive.execute(pk, competitive, token);
  }

  leaveMatch(
    matchId: string,
    token: string
  ): Observable<void> {
    return this.matchLeaveWS.execute(matchId, token);
  }
}
